import {addMonths} from 'date-fns/addMonths';
import {startOfMonth} from 'date-fns/startOfMonth';
import pick from 'lodash/pick';

import {any, buildQueryString, or} from '@edna/utils';

import {TData} from 'src/components/List';
import {EOperatorName, TChannelType, TItemId} from 'src/constants';
import {
  DEFAULT_ACTIVATION_TIME,
  ELicenseLabelType,
  ETariffDetailType,
  MOSCOW_TIMEZONE_OFFSET,
  TTariffDetail,
  TTariffDetailFullInfo,
  TTariffFullInfo,
} from 'src/containers/TariffsCatalog';
import {EDocumentType, ERoles} from 'src/containers/User';
import {CACHE_LIFETIME, ECacheTag, rootApi} from 'src/models';
import {combineDateAndTime, getAbsoluteDateTime} from 'src/utils';

import {
  DETAIL_PERIOD_MONTHS,
  TAvailableTariff,
  TAvailableTariffResponse,
  TConnectedTariff,
  TConnectedTariffResponse,
  TCreateAvailableTariffFormData,
  TTariffDetailFinalPrice,
  TTariffsLicensesFilters,
} from './definitions';

type TTariffLicenseDetail = TTariffDetailFullInfo & {catalogTariffName: string};

const OPERATOR_NAMES = Object.values(EOperatorName);

const getDateByMoscow = (localDate: Date) => {
  const absoluteDate = getAbsoluteDateTime(localDate);

  return new Date(`${absoluteDate}${MOSCOW_TIMEZONE_OFFSET}`);
};

const prepareTariffForSave = (tariff: TCreateAvailableTariffFormData) => ({
  ...pick(tariff, 'tariffCatalogId', 'tenantId', 'licenseDetailId'),
  data: {
    ...pick(tariff, 'license', 'amau'),
    [ELicenseLabelType.CONNECTION_FEE]: tariff.connectionFee,
  },
  activeFrom: new Date(),
});

const transformSmsPricingData = (
  data: TTariffFullInfo,
): {operators: TTariffDetailFullInfo[]; countries: TTariffDetailFullInfo[]} => {
  if (!data?.details?.length) {
    return {operators: [], countries: []};
  }

  const resultData = data.details?.reduce<{
    countries: Record<string, TTariffDetailFullInfo>;
    operators: TTariffDetailFullInfo[];
  }>(
    (result, item) => {
      if (OPERATOR_NAMES.includes(item.label as EOperatorName)) {
        return {...result, operators: [...result.operators, item]};
      }

      return {...result, countries: {...result.countries, [item.country]: item}};
    },
    {countries: {}, operators: []},
  );

  return {operators: resultData.operators, countries: Object.values(resultData.countries)};
};

const formatTariffDocumentType = (documentType?: EDocumentType | null) => {
  if (!documentType) {
    return;
  }

  return [EDocumentType.AGREEMENT, EDocumentType.TECH_PARTNER_OFFER].includes(documentType)
    ? EDocumentType.AGREEMENT
    : EDocumentType.CONTRACT;
};

const supportAccess = or<ERoles>('ROLE_ACCOUNT_MANAGER', 'ROLE_SUPPORT');

const tariffsApi = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getCatalogTariffDetails: builder.query<
      TTariffDetailFullInfo[],
      {id: number; detailTypes?: TTariffDetail['type'][]; countries?: TTariffDetail['country'][]}
    >({
      query: ({id, detailTypes, countries}) => ({
        method: 'POST',
        url: `/billing/tariffs/${id}/details/search${buildQueryString({
          onlyActive: true,
          detailTypes,
          countries,
        })}`,
        data: {
          limit: 250,
          offset: 0,
        },
        meta: {isShowError: true, roles: supportAccess},
      }),
      transformResponse: (data: TData<TTariffDetailFullInfo[]>) =>
        data ? data.content.flat() : [],
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getCatalogTariffLicense: builder.query<
      TTariffDetailFullInfo | undefined,
      {tariffId: number; licenseId: number}
    >({
      query: ({tariffId}) => ({
        method: 'POST',
        url: `/billing/tariffs/${tariffId}/details/search${buildQueryString({
          onlyActive: true,
          detailTypes: [ETariffDetailType.LICENSE],
        })}`,
        data: {
          limit: 100,
          offset: 0,
        },
        meta: {isShowError: true, roles: supportAccess},
      }),
      transformResponse: (data: TData<TTariffDetailFullInfo[]>, _, {licenseId}) =>
        data ? data.content.flat().find((detail) => detail.id === licenseId) : undefined,
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getAvailableTariffs: builder.query<
      TAvailableTariffResponse[],
      {channelType?: TChannelType; tenantId?: TItemId} | void
    >({
      query: (data) => ({
        method: 'GET',
        url: data?.tenantId
          ? `/billing/tariffs/available/support/find-all-active-and-planned/for-tenant/${data?.tenantId}`
          : '/billing/tariffs/available/find-all-active-and-planned/for-tenant',
        meta: {
          isShowError: true,
          roles: data?.tenantId
            ? or<ERoles>('ROLE_ACCOUNT_MANAGER', 'ROLE_SUPPORT', 'ROLE_AGENT')
            : any(),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      transformResponse: (data: TAvailableTariffResponse[], _, payload) =>
        payload?.channelType
          ? data?.filter((item) => item.active.channelType === payload.channelType)
          : data,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.TARIFFS_AVAILABLE];
        }

        return [
          ECacheTag.TARIFFS_AVAILABLE,
          ...result.map(({active}) => ({id: active.id, type: ECacheTag.TARIFFS_AVAILABLE})),
        ];
      },
    }),
    getAvailableActiveTariffs: builder.query<
      TAvailableTariff[],
      TChannelType | void | TChannelType[]
    >({
      query: () => ({
        method: 'GET',
        url: '/billing/tariffs/available/find-visible-active/for-tenant',
        meta: {isShowError: true},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      transformResponse: (data: TAvailableTariff[], _, channelType) =>
        channelType
          ? data?.filter((item) =>
              typeof channelType === 'string'
                ? item.channelType === channelType
                : channelType.includes(item.channelType),
            )
          : data,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.TARIFFS_AVAILABLE];
        }

        return [
          ECacheTag.TARIFFS_AVAILABLE,
          ...result.map(({id}) => ({id, type: ECacheTag.TARIFFS_AVAILABLE})),
        ];
      },
    }),
    getAvailableTariff: builder.query<TAvailableTariff, {id: TItemId; tenantId?: TItemId}>({
      query: ({id, tenantId}) => ({
        method: 'GET',
        url: tenantId
          ? `/billing/tariffs/available/support/find-by-id/${id}`
          : `/billing/tariffs/available/find-by-id/${id}`,
        meta: {isShowError: true, roles: tenantId ? supportAccess : any()},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      transformResponse: (data: TAvailableTariff[]) => data.find((item) => item.active) ?? data[0],
    }),
    changeAvailableTariff: builder.mutation<TAvailableTariff, TAvailableTariff>({
      query: ({name, ...data}) => ({
        method: 'PATCH',
        url: '/billing/tariffs/available',
        data,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Tariffs:successNotifications.changeTariff',
          i18nOptions: {name},
          roles: supportAccess,
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: ECacheTag.TARIFFS_AVAILABLE, id}],
    }),
    getConnectedTariffs: builder.query<TConnectedTariffResponse[], TItemId | void>({
      query: (tenantId) => ({
        method: 'GET',
        url: tenantId
          ? `/billing/tariffs/selected/support/find-all-active-and-planned/for-tenant/${tenantId}`
          : '/billing/tariffs/selected/find-all-active-and-planned/for-tenant',
        meta: {
          isShowError: true,
          roles: tenantId ? supportAccess : any(),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.TARIFFS_CONNECTED],
    }),
    changeConnectedTariff: builder.mutation<
      void,
      {
        newAvlTariffId: number;
        id: number;
        version: number;
        name: string;
        activeFrom?: Date;
      }
    >({
      query: ({name, activeFrom, ...data}) => ({
        method: 'POST',
        url: '/billing/tariffs/selected/change-tariff',
        data: {
          ...data,
          activeFrom: getDateByMoscow(
            activeFrom ??
              combineDateAndTime(startOfMonth(addMonths(new Date(), 1)), DEFAULT_ACTIVATION_TIME),
          ),
        },
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Tariffs:successNotifications.changeConnectedTariff',
          i18nOptions: {name},
          roles: supportAccess,
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_CONNECTED],
    }),
    cancelConnectedTariffChanging: builder.mutation<void, {id: number; version: number}>({
      query: (data) => ({
        method: 'DELETE',
        url: '/billing/tariffs/selected/soft-delete',
        data,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Tariffs:successNotifications.changeTariff',
          roles: supportAccess,
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_CONNECTED],
    }),
    getConnectedTariffHistoryBySubjectId: builder.query<TConnectedTariff[], number>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/billing/tariffs/selected/find-history-by-subject-id/${subjectId}`,
        meta: {
          isShowError: true,
          roles: supportAccess,
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    createAvailableTariff: builder.mutation<
      TAvailableTariff,
      TCreateAvailableTariffFormData | TCreateAvailableTariffFormData[]
    >({
      query: (values) => {
        const isArray = Array.isArray(values);

        return {
          method: 'POST',
          url: isArray
            ? '/billing/tariffs/available/create-all-by-tenant'
            : '/billing/tariffs/available/create-by-tenant',
          data: isArray ? values.map(prepareTariffForSave) : prepareTariffForSave(values),
          meta: {
            isShowError: true,
            roles: supportAccess,
          },
        };
      },
      invalidatesTags: [ECacheTag.TARIFFS_AVAILABLE],
    }),
    getTariffHistory: builder.query<TAvailableTariff[], number>({
      query: (id) => ({
        method: 'GET',
        url: `/billing/tariffs/available/support/find-history-by-id/${id}`,
        meta: {
          isShowError: true,
          roles: supportAccess,
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    deleteAvailableTariff: builder.mutation<void, {id: number; version: number}>({
      query: (tariff) => ({
        method: 'DELETE',
        url: '/billing/tariffs/available/soft-delete',
        data: {id: tariff.id, version: tariff.version},
        meta: {
          isShowError: true,
          roles: supportAccess,
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_AVAILABLE],
    }),
    hideAvailableTariff: builder.mutation<void, TItemId>({
      query: (id) => ({
        method: 'POST',
        url: '/billing/tariffs/available/hide',
        data: {id},
        meta: {
          isShowError: true,
          roles: supportAccess,
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_AVAILABLE],
    }),
    revealAvailableTariff: builder.mutation<void, TItemId>({
      query: (id) => ({
        method: 'POST',
        url: '/billing/tariffs/available/reveal',
        data: {id},
        meta: {
          isShowError: true,
          roles: supportAccess,
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_AVAILABLE],
    }),
    getTariffsLicenses: builder.query<TTariffLicenseDetail[], TTariffsLicensesFilters>({
      query: ({channelType, currency, resellerId, documentType}) => ({
        method: 'GET',
        url: `/billing/tariffs/search${buildQueryString({
          onlyActive: true,
          channelTypes: [channelType],
          detailTypes: [ETariffDetailType.LICENSE],
          currencies: [currency],
          resellerIds: [resellerId],
          documentTypes: [formatTariffDocumentType(documentType)],
        })}`,
        meta: {
          isShowError: true,
          roles: supportAccess,
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      transformResponse: (data: TTariffFullInfo[], _, {licensePeriod}) =>
        data?.reduce<TTariffLicenseDetail[]>((result, tariff) => {
          const licenses = tariff.details
            .filter((detail) => detail.data?.periodMonths === DETAIL_PERIOD_MONTHS[licensePeriod])
            .map((license) => ({...license, catalogTariffName: tariff.name}));

          return [...result, ...licenses];
        }, []),
    }),
    getTariffLicensesByIds: builder.query<TTariffDetail[], TItemId[]>({
      query: (detailIds) => ({
        method: 'GET',
        url: `/billing/tariffs/details/active-by-ids${buildQueryString({detailIds: [...new Set(detailIds)]})}`,
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getLicensePricing: builder.query<
      TTariffDetailFinalPrice,
      {tenantId?: TItemId; availableTariffId: TItemId}
    >({
      query: ({availableTariffId, tenantId}) => ({
        method: 'GET',
        url: tenantId
          ? `/billing/tariffs/support/active-license/for-tenant/${tenantId}`
          : '/billing/tariffs/active-license/for-tenant',
        params: {availableTariffId},
        meta: {
          roles: tenantId
            ? or<ERoles>('ROLE_ACCOUNT_MANAGER', 'ROLE_SUPPORT', 'ROLE_AGENT')
            : any(),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getCountriesPricing: builder.query<TTariffDetailFinalPrice[], TItemId>({
      query: (availableTariffId) => ({
        method: 'GET',
        url: '/billing/tariffs/active-regions/for-tenant',
        params: {availableTariffId},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getSmsTariffPricing: builder.query<
      {countries: TTariffDetailFullInfo[]; operators: TTariffDetailFullInfo[]},
      number
    >({
      query: (subjectId) => ({
        method: 'GET',
        url: `/billing/tariffs/sms/effective`,
        params: {subjectId},
      }),
      transformResponse: (data: TTariffFullInfo) => transformSmsPricingData(data),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
  }),
});

export const {
  useGetCatalogTariffDetailsQuery,
  useGetCatalogTariffLicenseQuery,
  useGetAvailableTariffsQuery,
  useGetAvailableActiveTariffsQuery,
  useGetAvailableTariffQuery,
  useChangeAvailableTariffMutation,
  useCreateAvailableTariffMutation,
  useGetConnectedTariffsQuery,
  useGetTariffsLicensesQuery,
  useGetTariffLicensesByIdsQuery,
  useChangeConnectedTariffMutation,
  useCancelConnectedTariffChangingMutation,
  useGetConnectedTariffHistoryBySubjectIdQuery,
  useGetTariffHistoryQuery,
  useDeleteAvailableTariffMutation,
  useHideAvailableTariffMutation,
  useRevealAvailableTariffMutation,

  useGetLicensePricingQuery,
  useGetCountriesPricingQuery,
  useGetSmsTariffPricingQuery,

  endpoints: {getAvailableActiveTariffs},
} = tariffsApi;
